import { clientEntity } from "./client-entity";
import { Model } from "@vuex-orm/core";
import { clientLocationEntity } from "./client-location-entity";
import { DateTime } from "luxon";

import { getBrowserIanaTimeZone as localTz } from "../../helper/browser-time-zone";

function ormDateStringToJs(value: unknown): Date | null {
  if (value != null) {
    return new Date(value as string);
  }
  return null;
}

export interface contactViewingEntity {
  contactId: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  emailAddress: string;
  contactStatusId?: number;
  clientLocationId?: number;
  address: string;
  createdOn: string;
  updatedOn: string;
  fullNameWithClientName: string;
  city: string;
  state: string;
  country: string;
  cellPhoneNo?: string;
  phone?: string;
  phoneExt?: string;
  clientId: number;
  clientLocationName: string;
  secondaryAddress?: string;
  jobTitle?: string;
  departmentName?: string;
  clientEntity: clientEntity;
  status: string;
  timeZone?: string;
  tick?: number;
  isActionButtonOpen: boolean; //will only be used for UI
  isFilling: boolean;
  isBillingContact: boolean;
  designationIds: boolean;
  isAccessible: boolean;
  isTemporaryContact: boolean;
  tagIds: number[];
}

export class contactViewingEntity extends Model {
  static entity = "contactViewingEntity";
  static primaryKey = "contactId";
  static fields() {
    return {
      contactId: this.number(0),
      firstName: this.string(""),
      middleName: this.string("").nullable(),
      lastName: this.string(""),
      emailAddress: this.string(""),
      contactStatusId: this.number(null).nullable(),
      clientLocationId: this.number(null).nullable(),
      address: this.string(""),
      createdOn: this.attr(""),
      updatedOn: this.attr(""),
      fullNameWithClientName: this.attr(""),
      city: this.string(""),
      state: this.string(""),
      country: this.string(""),
      cellPhoneNo: this.string("").nullable(),
      phone: this.string("").nullable(),
      phoneExt: this.string("").nullable(),
      clientId: this.number(0),
      clientLocationName: this.string(""),
      secondaryAddress: this.attr("").nullable(),
      jobTitle: this.string("").nullable(),
      departmentName: this.string("").nullable(),
      clientEntity: this.hasOne(clientEntity, "clientId", "clientId"),
      status: this.string(""),
      timeZone: this.attr(null),
      tick: this.number(0),
      isActionButtonOpen: this.boolean(false), //will only be used for UI
      isFilling: this.boolean(true),
      isBillingContact: this.boolean(null),
      designationIds: this.string(""),
      isAccessible: this.boolean(true),
      isTemporaryContact: this.boolean(false),
      tagIds: this.attr([]),
    };
  }

  static beforeSelect(records: any) {
    return records.filter((row: any) => row.contactId != 0);
  }

  get fullName() {
    let client = clientEntity
      .query<any>()
      .where("clientId", this.clientId)
      .first();
    if (client && client.clientName) {
      return (
        this.firstName + " " + this.lastName + " (" + client.clientName + ")"
      );
    }
    return this.firstName + " " + this.lastName;
  }

  get fullNameWithoutClientName() {
    return this.firstName + " " + this.lastName;
  }

  get clientName() {
    let client = clientEntity
      .query<any>()
      .where("clientId", this.clientId)
      .first();
    if (client && client.clientName) {
      return "(" + client.clientName + ")";
    }
    return "";
  }

  get fullNameWithoutClientNameForSort() {
    return this.firstName.toLowerCase() + " " + this.lastName.toLowerCase();
  }

  get jsCreatedOn() {
    return ormDateStringToJs(this.createdOn);
  }

  get jsUpdatedOn() {
    return ormDateStringToJs(this.updatedOn);
  }

  get clientLocationEntity() {
    if (this.clientLocationId != null && this.clientLocationId > 0) {
      return clientLocationEntity
        .query<any>()
        .where("clientLocationId", this.clientLocationId)
        .first();
    }
    return null;
  }

  get getTimeZone() {
    if (this.timeZone) {
      return this.timeZone;
    }
    if (this.clientLocationEntity) {
      return this.clientLocationEntity.timeZone;
    }
    return null;
  }

  get timeDifference() {
    let timezone = this.getTimeZone;
    if (timezone) {
      let currentTechnicianTime = DateTime.fromISO(DateTime.utc().toString(), {
        zone: "utc",
      }).setZone(localTz());
      let contactCurrentTime = DateTime.fromISO(DateTime.utc().toString(), {
        zone: "utc",
      }).setZone(timezone);

      let minutesDiff =
        currentTechnicianTime.offset - contactCurrentTime.offset;
      let isNegative = false;
      if (minutesDiff < 0) {
        isNegative = true;
        minutesDiff *= -1;
      }

      const hours = Math.floor(minutesDiff / 60);
      const minutes = minutesDiff % 60;
      if (hours == 0 && minutes == 0) {
        return "";
      }

      let timeDiff = isNegative ? "+" : "-";
      timeDiff += hours > 0 ? `${hours}hr ` : "";
      timeDiff += `${minutes > 0 ? `${minutes}min` : ""}`;

      return timeDiff;
    }

    return "";
  }

  get currentTime() {
    let timezone = this.getTimeZone;
    if (timezone) {
      let contactCurrentTime = DateTime.fromISO(DateTime.utc().toString(), {
        zone: "utc",
      })
        .setZone(timezone)
        .setLocale(navigator.language);
      let res = `${contactCurrentTime.toLocaleString(
        DateTime.DATE_MED,
      )} at ${contactCurrentTime.toLocaleString(DateTime.TIME_SIMPLE)}`;
      return res;
    }
    return "";
  }

  get fullNameWithEmail() {
    let fullName = `${this.fullNameWithoutClientName}`;
    if (this.emailAddress) {
      fullName += ` <${this.emailAddress}>`;
    }
    return fullName;
  }
}
